import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "../../../config/firebaseConfig";
import { fetchParentsStudents } from "../../../redux/features/parentStudentsSlice";
import { getStudioInfoForGrade } from "../utils/workspaceUtils";

/**
 * Helper to determine the studio environment for a given student object.
 */
export const resolveEnvForChild = (child) => {
  if (!child) return "scratch";
  if (child.workspace) {
    if (child.workspace === "scratch_jr" || child.workspace === "junior") {
      const subMode =
        child.juniorSubMode ||
        (child.grade === "Class 1" || child.class === "Class 1" ? "basic" : "advanced");
      return subMode === "advanced" ? "junior_blocks_adv" : "junior_blocks_basic";
    }
    return child.workspace;
  }
  const activeGrade = child.grade || child.studentClass || child.class;
  const studioInfo = getStudioInfoForGrade(activeGrade);
  if (studioInfo.type === "python") {
    return "python";
  } else if (studioInfo.type === "junior") {
    const subMode =
      child.juniorSubMode ||
      (activeGrade === "Class 1" || activeGrade === 1 ? "basic" : "advanced");
    return subMode === "advanced" ? "junior_blocks_adv" : "junior_blocks_basic";
  }
  return "scratch";
};

/**
 * Hook to manage student accounts, parent-child selection, and studio environment resolution.
 */
export const useWorkspaceStudent = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const childIdParam = searchParams.get("childId");

  const { userDetails: parentsDetails } = useSelector((state) => state.auth);
  const { students: reduxStudents } = useSelector(
    (state) => state.parentStudents || { students: [] }
  );

  const isStudentAccount = parentsDetails?.accountType === "student";

  // Resolve initial child synchronously from auth or redux to prevent environment flipping on mount
  const initialChild = useMemo(() => {
    if (isStudentAccount && parentsDetails) return parentsDetails;
    if (Array.isArray(reduxStudents) && reduxStudents.length > 0) {
      if (childIdParam) {
        const found = reduxStudents.find(
          (c) => c.id === childIdParam || c.uid === childIdParam
        );
        if (found) return found;
      }
      return reduxStudents[0];
    }
    return null;
  }, [isStudentAccount, parentsDetails, reduxStudents, childIdParam]);

  const [childrenList, setChildrenList] = useState(() => (initialChild ? [initialChild] : []));
  const [selectedChild, setSelectedChild] = useState(initialChild);
  const [activeWorkspaceEnv, setActiveWorkspaceEnv] = useState(() =>
    resolveEnvForChild(initialChild)
  );
  const [showAddChildModal, setShowAddChildModal] = useState(false);

  const initEnvForChild = useCallback((child) => {
    if (!child) return;
    const resolvedEnv = resolveEnvForChild(child);
    setActiveWorkspaceEnv(resolvedEnv);
  }, []);

  // Fetch children from Firestore students collection or student account session
  useEffect(() => {
    const fetchChildren = async () => {
      const parentId =
        parentsDetails?.id || parentsDetails?.uid || auth?.currentUser?.uid;
      if (!parentId) return;

      // If logged in directly as student, isolate context to this student
      if (isStudentAccount) {
        setChildrenList([parentsDetails]);
        setSelectedChild(parentsDetails);
        initEnvForChild(parentsDetails);
        return;
      }

      try {
        const q = query(
          collection(db, "students"),
          where("parentUid", "==", parentId)
        );
        const snap = await getDocs(q);
        const list = [];
        snap.docs.forEach((docSnap) => {
          list.push({ id: docSnap.id, ...docSnap.data() });
        });

        if (list.length > 0) {
          setChildrenList(list);
          if (childIdParam) {
            const found = list.find(
              (c) => c.id === childIdParam || c.uid === childIdParam
            );
            const target = found || list[0];
            setSelectedChild(target);
            initEnvForChild(target);
          } else {
            setSelectedChild(list[0]);
            initEnvForChild(list[0]);
          }
        } else if (Array.isArray(reduxStudents) && reduxStudents.length > 0) {
          setChildrenList(reduxStudents);
          if (childIdParam) {
            const found = reduxStudents.find(
              (c) => c.id === childIdParam || c.uid === childIdParam
            );
            const target = found || reduxStudents[0];
            setSelectedChild(target);
            initEnvForChild(target);
          } else {
            setSelectedChild(reduxStudents[0]);
            initEnvForChild(reduxStudents[0]);
          }
        }
      } catch (err) {
        console.error("Error fetching students in workspace:", err);
      }
    };

    fetchChildren();
  }, [parentsDetails, childIdParam, reduxStudents, isStudentAccount, initEnvForChild]);

  const handleChildAdded = useCallback(
    (newChild) => {
      setChildrenList((prev) => [...prev, newChild]);
      setSelectedChild(newChild);
      if (newChild.id) {
        setSearchParams({ childId: newChild.id });
      }
      const parentId =
        parentsDetails?.id || parentsDetails?.uid || auth?.currentUser?.uid;
      if (parentId) {
        dispatch(fetchParentsStudents({ parentUid: parentId }));
      }
    },
    [parentsDetails, dispatch, setSearchParams]
  );

  return {
    parentsDetails,
    childrenList,
    selectedChild,
    setSelectedChild,
    activeWorkspaceEnv,
    setActiveWorkspaceEnv,
    isStudentAccount,
    showAddChildModal,
    setShowAddChildModal,
    handleChildAdded,
    initEnvForChild,
  };
};

export default useWorkspaceStudent;
